import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { fetchSnowflakeColumns, SnowflakeColumn } from '../../../../services/queries/SnowflakeSyncQueries';

interface SnowflakeColumnAutocompleteProps {
  label: string;
  required?: boolean;
  field: {
    value?: string;
    onChange: (value: string | undefined) => void;
    onBlur: () => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
  options: SnowflakeColumn[];
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
}

/**
 * Autocomplete picker for a Snowflake column. Used wherever a config field needs to
 * resolve to a column name in the configured Snowflake source table — the identifier
 * column (entity mapping), reporting period columns, etc.
 *
 * Pair with `useSnowflakeColumnOptions()` to fetch + manage the options/loading/error
 * state from the current form's `connectionId` + `sourceTable` watches.
 */
export function SnowflakeColumnAutocomplete({
  label,
  required,
  field,
  options,
  disabled,
  loading,
  error,
  helperText,
}: SnowflakeColumnAutocompleteProps) {
  return (
    <Autocomplete
      options={options.map((c) => c.name)}
      value={field.value ?? null}
      onChange={(_, next) => field.onChange(next ?? undefined)}
      getOptionLabel={(opt) => {
        const match = options.find((c) => c.name === opt);
        return match ? `${match.name} — ${match.type}` : opt;
      }}
      disabled={disabled}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={error ? 'Failed to load columns for this table' : helperText}
          onBlur={field.onBlur}
          inputRef={field.ref}
          name={field.name}
        />
      )}
    />
  );
}

/**
 * Hook that watches the current form's snowflake connection + table and returns the
 * column list (plus loading/error flags) for use with `<SnowflakeColumnAutocomplete>`.
 * Returns empty options when no snowflake connection/table is configured yet.
 */
export function useSnowflakeColumnOptions() {
  const { watch } = useFormContext();
  const connectionId = watch('config.snowflakeConfig.connectionId') as number | undefined;
  const sourceTable = watch('config.snowflakeConfig.sourceTable') as string | undefined;

  const columnsQuery = useQuery({
    queryKey: ['snowflake-columns', connectionId, sourceTable],
    queryFn: () => fetchSnowflakeColumns(connectionId!, sourceTable!),
    enabled: connectionId !== undefined && connectionId !== null && !!sourceTable,
  });

  return {
    options: (columnsQuery.data ?? []) as SnowflakeColumn[],
    loading: columnsQuery.isLoading,
    error: columnsQuery.isError,
    /** Disable picker until a table is selected (no columns to choose from). */
    disabled: !sourceTable,
    hasTable: !!sourceTable,
  };
}
